/**
 * Iris Cart Bundle Manager
 * Groups Iris device, digital plan, and warranty as unified bundles
 * Syncs quantities and provides single control point
 */

class IrisCartBundleManager {
  constructor() {
    console.log('=== Iris Cart Bundle Manager Initialized ===');
    
    this.productIds = {
      device: 10015575867576,
      plans: {
        '1year': 10018506047672,
        '2years': 10004176470200,
        '3years': 10004188758200
      },
      warranties: {
        '2years': 10122467934392,
        '3years': 10122524950712
      },

      softwareRenewal: 10139024294072
    };
    
    this.planWarrantyMap = {
      '1year': null,
      '2years': '2years',
      '3years': '3years'
    };
    
    this.bundles = [];
    this.cart = null;
    this.init();
    this.isReInitializing = false;
  }

  async init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    console.log('Setting up cart bundle management');

    if (!window.location.pathname.includes('/cart')) {
      console.log('Not on cart page');
      return;
    }

    await this.loadCart();
    await this.normalizeQuantities();
    this.analyzeBundles();
    this.renderBundles();

    // Listen for cart updates from non-bundle items
    this.setupCartUpdateListeners();

    console.log('=== Setup Complete ===');
  }

  setupCartUpdateListeners() {
    console.log('🎯 Setting up cart update listeners...');
    // Track if we've already scheduled a re-initialization
    this.reInitTimeout = null;
    
    // Intercept Shopify cart fetches
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const url = args[0];
      
      if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update'))) {
        console.log('🌐 Cart update detected');
        
        return originalFetch.apply(this, args).then(response => {
          // Clear any pending timeout
          if (self.reInitTimeout) {
            clearTimeout(self.reInitTimeout);
          }
          
          // Schedule re-initialization after Shopify finishes updating the DOM
          self.reInitTimeout = setTimeout(() => {
            console.log('⏰ Re-initializing after cart update');
            self.reInitialize();
          }, 1000);
          
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };
    
    console.log('✅ Fetch interceptor installed');
  }

  async reInitialize() {
    // Prevent multiple simultaneous re-initializations
    if (this.isReInitializing) {
      console.log('⚠️ Re-initialization already in progress, skipping...');
      return;
    }
    
    this.isReInitializing = true;
    console.log('🔄 === Re-initializing Bundle Manager ===');
    
    console.log('🧹 Clearing existing bundle cards');
    // Clear existing bundle cards
    document.querySelectorAll('[id^="IrisBundle-"]').forEach(el => el.remove());
    
    console.log('👁️ Showing hidden items');
    // Show any hidden items first
    this.cart.items.forEach((item, index) => {
      const cartItemIndex = index + 1;
      const selectors = [
        `#CartItem-${cartItemIndex}`,
        `[data-index="${cartItemIndex}"]`
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) {
          const row = element.closest('tr, li, .cart-item');
          if (row) {
            row.style.display = '';
          }
        }
      }
    });
    
    console.log('📦 Re-running bundle logic');
    // Re-run the bundle logic
    await this.loadCart();
    await this.normalizeQuantities();
    this.analyzeBundles();
    this.renderBundles();
    
    console.log('✅ === Re-initialization Complete ===');
    this.isReInitializing = false;
  }


  async loadCart() {
    const response = await fetch('/cart.js');
    this.cart = await response.json();
    console.log('Cart loaded:', this.cart.items.length, 'items');
  }

  async normalizeQuantities() {
    console.log('Normalizing bundle quantities...');
    
    // Check if cart has any bundle items at all
    const hasBundleItems = this.cart.items.some(item => 
      item.product_id === this.productIds.device || 
      Object.values(this.productIds.plans).includes(item.product_id) ||
      Object.values(this.productIds.warranties).includes(item.product_id) ||
      item.product_id === this.productIds.softwareRenewal
    );
    
    if (!hasBundleItems) {
      console.log('No bundle items in cart, skipping normalization');
      return;
    }

    // Group items into potential bundles by variant IDs
    // Each unique device variant should have its own bundle
    const devicesByVariant = {};
    
    this.cart.items.forEach(item => {
      if (item.product_id === this.productIds.device) {
        const variantId = item.variant_id;
        if (!devicesByVariant[variantId]) {
          devicesByVariant[variantId] = [];
        }
        devicesByVariant[variantId].push(item);
      }
    }
  );
    
    console.log('Devices grouped by variant:', Object.keys(devicesByVariant).length, 'different variants');
    
    // For each device variant, match with plan and warranty
    const updates = {};
    let needsUpdate = false;
    
    Object.values(devicesByVariant).forEach(devices => {
      devices.forEach(device => {
        // Find plan for this device
        let foundPlan = null;
        let planYear = null;
        
        for (const [year, planId] of Object.entries(this.productIds.plans)) {
          const planItem = this.cart.items.find(item => 
            item.product_id === planId &&
            item.quantity === device.quantity // Match by quantity to pair them
          );
          
          if (planItem) {
            foundPlan = planItem;
            planYear = year;
            break;
          }
        }
        
        if (!foundPlan) {
          // Try without quantity match
          for (const [year, planId] of Object.entries(this.productIds.plans)) {
            const planItem = this.cart.items.find(item => item.product_id === planId);
            if (planItem) {
              foundPlan = planItem;
              planYear = year;
              break;
            }
          }
        }
        
        if (foundPlan) {
          const warrantyYear = this.planWarrantyMap[planYear];
          let foundWarranty = null;
          
          if (warrantyYear) {
            const warrantyId = this.productIds.warranties[warrantyYear];
            foundWarranty = this.cart.items.find(item =>
              item.product_id === warrantyId &&
              item.quantity === device.quantity
            );
            
            if (!foundWarranty) {
              foundWarranty = this.cart.items.find(item => item.product_id === warrantyId);
            }
          }

          let foundSoftwareRenewal = this.cart.items.find(item =>
            item.product_id === this.productIds.softwareRenewal &&
            item.quantity === device.quantity
          );
          
          if (!foundSoftwareRenewal) {
            foundSoftwareRenewal = this.cart.items.find(item => 
              item.product_id === this.productIds.softwareRenewal
            );
          }
          
          // Sync quantities within this bundle
          const quantities = [device.quantity, foundPlan.quantity];
          if (foundWarranty) quantities.push(foundWarranty.quantity);
          if (foundSoftwareRenewal) quantities.push(foundSoftwareRenewal.quantity);
          
          const maxQty = Math.max(...quantities);
          
          if (device.quantity !== maxQty) {
            updates[device.key] = maxQty;
            needsUpdate = true;
          }
          if (foundPlan.quantity !== maxQty) {
            updates[foundPlan.key] = maxQty;
            needsUpdate = true;
          }
          if (foundWarranty && foundWarranty.quantity !== maxQty) {
            updates[foundWarranty.key] = maxQty;
            needsUpdate = true;
          }
          if (foundSoftwareRenewal && foundSoftwareRenewal.quantity !== maxQty) {
            updates[foundSoftwareRenewal.key] = maxQty;
            needsUpdate = true;
          }
        }
      });
    });

    if (needsUpdate) {
      console.log('Syncing mismatched quantities:', updates);
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      await this.loadCart();
    }
  }

  identifyBundleSets() {
    console.log('\n=== Identifying Bundle Sets ===');
    const sets = [];
    const usedItems = new Set();
    
    // Log all cart items first
    console.log('All cart items:');
    this.cart.items.forEach((item, i) => {
      console.log(`  ${i}: ${item.product_title} (ID: ${item.product_id}, Qty: ${item.quantity}, Key: ${item.key})`);
    });
    
    // Find all devices first
    const devices = this.cart.items.filter(item => item.product_id === this.productIds.device);
    console.log(`\nFound ${devices.length} device(s)`);
    
    devices.forEach((device, deviceIdx) => {
      console.log(`\n--- Processing Device ${deviceIdx + 1} ---`);
      console.log(`Device: ${device.product_title}, Key: ${device.key}`);
      
      if (usedItems.has(device.key)) {
        console.log('⚠️ Device already used, skipping');
        return;
      }
      
      const bundle = { device, plan: null, warranty: null, softwareRenewal: null, planYear: null };
      
      // Try to find a matching plan (that hasn't been used yet)
      let foundPlan = false;
      for (const [year, planId] of Object.entries(this.productIds.plans)) {
        console.log(`  Checking for ${year} plan (ID: ${planId})`);
        
        const planItem = this.cart.items.find(item => 
          !usedItems.has(item.key) && 
          item.product_id === planId
        );
        
        if (planItem) {
          console.log(`  ✓ Found ${year} plan: ${planItem.product_title}`);
          bundle.plan = planItem;
          bundle.planYear = year;
          usedItems.add(planItem.key);
          foundPlan = true;
          
          // Now find matching warranty for this specific plan year
          const warrantyYear = this.planWarrantyMap[year];
          console.log(`  Looking for ${warrantyYear} warranty`);
          
          if (warrantyYear) {
            const warrantyId = this.productIds.warranties[warrantyYear];
            console.log(`  Checking warranty ID: ${warrantyId}`);
            
            const warrantyItem = this.cart.items.find(item =>
              !usedItems.has(item.key) && 
              item.product_id === warrantyId
            );
            
            if (warrantyItem) {
              console.log(`  ✓ Found warranty: ${warrantyItem.product_title}`);
              bundle.warranty = warrantyItem;
              usedItems.add(warrantyItem.key);
            } else {
              console.log(`  ✗ No warranty found`);
            }
          } else {
            console.log(`  No warranty needed for ${year} plan`);
          }

          console.log(`  Looking for Software Renewal subscription`);
          const softwareRenewalItem = this.cart.items.find(item =>
            !usedItems.has(item.key) && 
            item.product_id === this.productIds.softwareRenewal
          );
          
          if (softwareRenewalItem) {
            console.log(`  ✓ Found Software Renewal: ${softwareRenewalItem.product_title}`);
            bundle.softwareRenewal = softwareRenewalItem;
            usedItems.add(softwareRenewalItem.key);
          } else {
            console.log(`  ✗ No Software Renewal found`);
          }
          
          break; // Stop looking for plans once we found one
        }
      }
      
      if (foundPlan) {
        console.log(`✓ Created bundle: Device + ${bundle.planYear} + ${bundle.warranty ? 'Warranty' : 'No Warranty'} + ${bundle.softwareRenewal ? 'Software Renewal' : 'No Software Renewal'}`);
        usedItems.add(device.key);
        sets.push(bundle);
      } else {
        console.log('✗ No plan found for this device');
      }
    });
    
    console.log(`\n=== Total bundles created: ${sets.length} ===`);
    console.log('Used items:', Array.from(usedItems));
    
    return sets;
  }

  analyzeBundles() {
    this.bundles = this.identifyBundleSets();
    console.log(`Found ${this.bundles.length} bundle(s)`);
    this.bundles.forEach((b, i) => {
       console.log(`Bundle ${i + 1}:`, {
        device: b.device.product_title,
        plan: b.plan.product_title,
        warranty: b.warranty?.product_title || 'None',
        softwareRenewal: b.softwareRenewal?.product_title || 'None',
        quantity: b.device.quantity
      });
    });
  }

  renderQuantityWarning() {
  const totalIrisQty = this.getTotalIrisQuantity();
  const warningId = 'iris-quantity-warning';
  let warningDiv = document.getElementById(warningId);
  
  // Remove warning if below limit
  if (totalIrisQty < 5) {
    if (warningDiv) warningDiv.remove();
    return;
  }

  // Warning already exists, nothing to do
  if (warningDiv) return;

  // Create and insert warning
  const cartForm = document.querySelector('form[action="/cart"]');
  if (!cartForm) return;

  warningDiv = document.createElement('div');
  warningDiv.id = warningId;
  warningDiv.style.cssText = `
    border: 1px solid #24B0FF;
    border-radius: 15px;
    padding: 15px 15px;
    margin-top:40px;
    margin-left: auto;
    color: #121212BF;
    font-size: 12px;
    line-height: 1.2;
    width: 350px;
  `;
  
  warningDiv.innerHTML = `
  <p style="margin: 0; font-size: 19px; margin-bottom: 5px; display: flex; align-items: center; gap: 8px;">
    <img src="https://cdn.shopify.com/s/files/1/0778/3802/4888/files/info_15238131.png?v=1762545509" alt="" style="width: 25px; height: 25px;" />
    Need more than 5?
  </p>
  <p style="margin: 0">
    Contact our sales team at <a href="mailto:salesoperations@exo.inc" style="color: #24B0FF; text-decoration: underline;">salesoperations@exo.inc</a>
  </p>
`;
  
  const checkoutButton = cartForm.querySelector('[name="checkout"]');
  if (checkoutButton) {
    checkoutButton.parentElement.insertBefore(warningDiv, checkoutButton);
  } else {
    cartForm.appendChild(warningDiv);
  }
}
// Deactivate Checkout Button for more than 5 Iris:
updateCheckoutButton() {
  const totalIrisQty = this.getTotalIrisQuantity();
  const checkoutButton = document.querySelector('button[name="checkout"], input[name="checkout"]');
  
  if (!checkoutButton) return;
  
  if (totalIrisQty > 5) {
    checkoutButton.disabled = true;
    checkoutButton.style.opacity = '0.5';
    checkoutButton.style.cursor = 'not-allowed';
  } else {
    checkoutButton.disabled = false;
    checkoutButton.style.opacity = '1';
    checkoutButton.style.cursor = 'pointer';
  }
}

  renderBundles() {
    if (this.bundles.length === 0) return;

    const cartForm = document.querySelector('form[action="/cart"]');
    if (!cartForm) return;

    // // Find the cart items container
    // const itemsContainer = cartForm.querySelector('.cart-items tbody, .cart-items');
    // if (!itemsContainer) return;

    // Find the cart items container - specifically target tbody
    const itemsContainer = cartForm.querySelector('.cart-items tbody');
    if (!itemsContainer) {
      console.warn('Could not find cart items tbody, trying alternate selector');
      // Fallback to the old selector if tbody not found
      const fallback = cartForm.querySelector('.cart-items');
      if (!fallback) return;
      itemsContainer = fallback;
    }

    console.log('✅ Found items container:', itemsContainer.tagName);

    // Hide all bundle items
    this.bundles.forEach(bundle => {
      this.hideCartItem(bundle.device.key);
      this.hideCartItem(bundle.plan.key);
      if (bundle.warranty) this.hideCartItem(bundle.warranty.key);
      // NEW: Hide Software Renewal from cart display
      if (bundle.softwareRenewal) this.hideCartItem(bundle.softwareRenewal.key);
    });

    // Create bundle cards
    this.bundles.forEach((bundle, index) => {
      const bundleCard = this.createBundleCard(bundle, index);
      itemsContainer.insertBefore(bundleCard, itemsContainer.firstChild);
    });

    // Show quantity warning if needed
    this.renderQuantityWarning();

    // Update checkout button state
    this.updateCheckoutButton();
  }

  hideCartItem(key) {
    const index = this.cart.items.findIndex(item => item.key === key) + 1;
    const selectors = [
      `#CartItem-${index}`,
      `[data-index="${index}"]`,
      `[data-key="${key}"]`
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        const row = element.closest('tr, li, .cart-item');
        if (row) {
          row.style.display = 'none';
          return;
        }
      }
    }
  }

  createBundleCard(bundle, index) {
  const yearDisplay = { '1year': '1-Year', '2years': '2-Year', '3years': '3-Year' }[bundle.planYear];
  // UPDATED: Total price calculation includes Software Renewal
  // Note: Software Renewal should be $0 due to 100% discount, but we include it for completeness
  const totalPrice = (
    bundle.device.final_line_price + 
    bundle.plan.final_line_price + 
    (bundle.warranty?.final_line_price || 0) +
    (bundle.softwareRenewal?.final_line_price || 0)  // Should be $0 with discount
  ) / 100;

  const row = document.createElement('tr');
  row.className = 'cart-item';
  row.id = `IrisBundle-${index + 1}`;
   
  // Check if total Iris quantity is at max
  const totalIrisQty = this.getTotalIrisQuantity();
  const isAtMaxQuantity = totalIrisQty >= 5;
  
  row.innerHTML = `
    <td class="cart-item__media">
      <a href="${bundle.plan.url}" class="cart-item__link" aria-hidden="true" tabindex="-1"> </a>
      <div class="cart-item__image-container gradient global-media-settings">
        <img
          src="${bundle.plan.image}"
          class="cart-item__image"
          alt="${bundle.plan.product_title}"
          loading="lazy"
          width="150"
          height="150"
        >
      </div>
    </td>
    
    <td class="cart-item__details">
      <a href="/products/buy-exo-iris" class="cart-item__name h4 break">
        ${yearDisplay} Plan
      </a>
      <div class="product-option">
      <p> Includes Exo Iris, AI software, Exo Cloud services, and ${bundle.warranty ? `${yearDisplay.toLowerCase()} warranty` : '1-year warranty'}.  </p>
      </div>
    </td>
    
    <td class="cart-item__totals right medium-hide large-up-hide">
      <div class="cart-item__price-wrapper">
        <span class="price price--end">$${totalPrice.toFixed(2)}</span>
      </div>
    </td>
    
    <td class="cart-item__quantity">
    <div class="cart-item__quantity-wrapper">
<quantity-input class="quantity cart-quantity">
          <button class="quantity__button" name="minus" type="button" data-action="decrease" data-bundle="${index}">
            <span class="visually-hidden">Decrease quantity</span>
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"/>
              </svg>
            </span>
          </button>
          <input 
            class="quantity__input" 
            type="number" 
            name="updates[]"
            value="${bundle.device.quantity}" 
            min="1" 
            data-bundle="${index}"
            readonly
          >
          <button class="quantity__button" name="plus" type="button" data-action="increase" data-bundle="${index}" ${isAtMaxQuantity ? 'disabled style="opacity: 0.3; cursor: not-allowed;"' : ''}>
            <span class="visually-hidden">Increase quantity</span>
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"/>
              </svg>
            </span>
          </button>
        </quantity-input>
    <cart-remove-button>
      <button class="button button--tertiary" type="button" data-bundle="${index}">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon icon-remove">
          <path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path>
          <path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"></path>
        </svg>
      </button>
    </cart-remove-button>
  </div>
</td>
    
    <td class="cart-item__totals right small-hide">
      <div class="cart-item__price-wrapper">
        <span class="price price--end">$${totalPrice.toFixed(2)}</span>
      </div>
    </td>
  `;

  // // Add event listeners
  // row.querySelectorAll('[data-action]').forEach(btn => {
  //   btn.addEventListener('click', (e) => {
  //     e.preventDefault();
  //     this.handleQuantityChange(e, bundle);
  //   });
  // });

  // row.querySelector('cart-remove-button button').addEventListener('click', (e) => {
  //   e.preventDefault();
  //   this.handleRemove(e, bundle);
  // });
  row.querySelectorAll('button[data-action="decrease"], button[data-action="increase"]').forEach(btn => {
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleQuantityChange(e, bundle);
  });
});

row.querySelector('cart-remove-button button').addEventListener('click', (e) => {
  e.preventDefault();
  e.stopPropagation();
  this.handleRemove(e, bundle);
});

  return row;
}
  getTotalIrisQuantity() {
  return this.bundles.reduce((total, bundle) => {
    return total + bundle.device.quantity;
  }, 0);
 }
  async handleQuantityChange(e, bundle) {
    e.preventDefault();
    // const action = e.target.dataset.action;
    const button = e.currentTarget;
    const action = button.dataset.action;
    const currentQty = bundle.device.quantity;
    const totalIrisQty = this.getTotalIrisQuantity();

    const newQty = action === 'increase' ? Math.min(currentQty + 1, 5) : Math.max(0, currentQty - 1);

    const updates = {
      [bundle.device.key]: newQty,
      [bundle.plan.key]: newQty
    };
    if (bundle.warranty) {
      updates[bundle.warranty.key] = newQty;
    }
    if (bundle.softwareRenewal) {
      updates[bundle.softwareRenewal.key] = newQty;
    }

    await this.updateCart(updates);
  }

  async handleRemove(e, bundle) {
    e.preventDefault();
    // if (!confirm('Remove this bundle from cart?')) return;

    const updates = {
      [bundle.device.key]: 0,
      [bundle.plan.key]: 0
    };
    if (bundle.warranty) {
      updates[bundle.warranty.key] = 0;
    }
    if (bundle.softwareRenewal) {
      updates[bundle.softwareRenewal.key] = 0;
    }

    await this.updateCart(updates);
  }

  async updateCart(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Update failed:', error);
      alert('Failed to update cart. Please try again.');
    }
  }
}

// Initialize
console.log('Iris Cart Bundle Manager script loaded');
new IrisCartBundleManager();